import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useId, useState, type MouseEvent } from 'react';

import { testIdAttr } from '@/lib/testIds';

import { Button } from './Button';

export interface RowAction {
  /**
   * Names the item for the testing team's tests, which find it as
   * `<menu testId>-<id>-item`. Lowercase words and dashes, such as
   * `change-status`; never the label, which may be reworded.
   */
  id: string;
  /** The words on the menu item. Also how the portal's own tests find it. */
  label: string;
  onSelect: () => void;
  /** Greys the item out and stops it being chosen. */
  disabled?: boolean;
  /**
   * Why a disabled item is off, shown under its label and read as its
   * description; its test ID is `<menu testId>-<id>-hint`. Ignored while the
   * item is enabled.
   */
  hint?: string;
}

interface RowActionsMenuProps {
  /**
   * What this row is about - an organization's name, a person's name. It goes
   * into the control's accessible name, so a screen reader says whose actions
   * these are rather than reading "Actions" forty times down a table.
   */
  rowLabel: string;
  actions: RowAction[];
  /**
   * Names the menu for the testing team's tests: the button is
   * `<testId>-button`, the menu `<testId>-menu`. Inside a table row it need
   * only be unique within the row. See docs/test-ids.md.
   */
  testId: string;
  /**
   * What opens the menu: three vertical dots (the default), three dots in a
   * box, or a button reading "Actions" - the ARC Builder's wireframe draws
   * the last two.
   */
  trigger?: 'dots' | 'boxed-dots' | 'button';
}

/**
 * The actions for one row of a table, behind one control.
 *
 * Shared rather than per-feature because two sections need the same thing and
 * neither may import from the other: the organizations list uses it for Open
 * and Change status, and the users list for the things done to a person.
 *
 * MUI's Menu carries the keyboard behaviour the pattern requires: the arrow
 * keys move between items, Escape closes the menu, and focus returns to the
 * control that opened it.
 */
export function RowActionsMenu({
  rowLabel,
  actions,
  testId,
  trigger = 'dots',
}: RowActionsMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const id = useId();
  const menuId = `${id}-menu`;
  const buttonId = `${id}-button`;
  const open = anchor !== null;

  function choose(action: RowAction) {
    // Close first. The item's own work may open a dialog, and focus has to be
    // back on the button before that happens or it lands nowhere.
    setAnchor(null);
    action.onSelect();
  }

  const opener = {
    id: buttonId,
    'aria-label': `Actions for ${rowLabel}`,
    'aria-haspopup': 'menu' as const,
    'aria-controls': open ? menuId : undefined,
    'aria-expanded': open ? true : undefined,
    'data-testid': `${testId}-button`,
    onClick: (event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget),
  };

  return (
    <>
      {trigger === 'button' ? (
        <Button variant="secondary" size="sm" {...opener}>
          Actions
        </Button>
      ) : (
        <IconButton
          {...opener}
          size="small"
          sx={
            trigger === 'boxed-dots'
              ? { border: 1.5, borderColor: 'text.primary', borderRadius: 1, p: 0.25 }
              : undefined
          }
        >
          {trigger === 'boxed-dots' ? (
            <MoreHorizIcon fontSize="small" />
          ) : (
            <MoreVertIcon fontSize="small" />
          )}
        </IconButton>
      )}

      <Menu
        id={menuId}
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        // Names the menu after the row too, so the list a screen reader opens
        // is tied to the row it came from.
        slotProps={{
          list: { 'aria-labelledby': buttonId },
          paper: testIdAttr(`${testId}-menu`),
        }}
      >
        {actions.map((action) => {
          const hint = action.disabled ? action.hint : undefined;
          const labelId = `${id}-${action.id}-label`;
          const hintId = `${id}-${action.id}-hint`;
          return (
            <MenuItem
              key={action.id}
              data-testid={`${testId}-${action.id}-item`}
              disabled={action.disabled}
              onClick={() => choose(action)}
              // Named by its label alone; the reason it is off is its description.
              aria-labelledby={hint ? labelId : undefined}
              aria-describedby={hint ? hintId : undefined}
              sx={
                hint ? { flexDirection: 'column', alignItems: 'flex-start' } : undefined
              }
            >
              <span id={labelId}>{action.label}</span>
              {hint ? (
                <Typography
                  id={hintId}
                  variant="caption"
                  color="text.secondary"
                  data-testid={`${testId}-${action.id}-hint`}
                  sx={{ whiteSpace: 'normal', maxWidth: '20rem' }}
                >
                  {hint}
                </Typography>
              ) : null}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
